import type { Object3D } from 'three';
import type {
	SketchEntityReferencePreviewBatchResult,
	SketchEntityReferencePreviewCoordinatorDependencies,
	SketchEntityReferencePreviewObject,
	SketchEntityReferencePreviewResult,
} from '../../../../../types/reference/sketch-entity-reference/SketchEntityReferencePreviewCoordinator';
import type { CreateGeometryReferencesInput } from '../../model/SketchEntityReferenceTypes';

export type {
	SketchEntityReferencePreviewBatchResult,
	SketchEntityReferencePreviewCoordinatorDependencies,
	SketchEntityReferencePreviewObject,
	SketchEntityReferencePreviewResult,
} from '../../../../../types/reference/sketch-entity-reference/SketchEntityReferencePreviewCoordinator';

/**
 * 草图实体引用命令级 Preview 协调器。
 *
 * 设计说明：
 * 1. 每次 Convert Entities 命令必须独占一个实例；
 * 2. revision 阻止过期异步结果重新进入场景；
 * 3. BatchService 显式提供完整对象所有权清单，本类不猜测草图对象结构；
 * 4. 场景摘除和资源释放统一委托给退役端口。
 */
export class SketchEntityReferencePreviewCoordinator {
	/** 当前有效 Preview 根实体。 */
	private previewEntities: SketchEntityReferencePreviewObject[] = [];
	/** 当前批次拥有并必须一起退役的全部场景对象。 */
	private previewObjects: Object3D[] = [];
	/** 异步请求版本号。 */
	private previewRevision = 0;
	/** 当前命令级协调器是否已经结束生命周期。 */
	private disposed = false;

	/**
	 * 创建命令级 Preview 协调器。
	 *
	 * @param dependencies Preview 构建和对象退役能力。
	 */
	public constructor(
		private readonly dependencies: SketchEntityReferencePreviewCoordinatorDependencies,
	) {}

	/**
	 * 刷新当前 Preview。
	 *
	 * @param createInput 转换实体引用创建输入。
	 * @returns 当前仍有效的 Preview 结果。
	 */
	public async refreshPreview(
		createInput: CreateGeometryReferencesInput,
	): Promise<SketchEntityReferencePreviewResult> {
		this.assertActive();
		const requestRevision = ++this.previewRevision;
		this.retireTrackedPreviewObjects();

		const batchResult = await this.dependencies.buildPreviewBatch(createInput);
		if (this.disposed || requestRevision !== this.previewRevision) {
			// 过期结果从未取得当前批次所有权，只允许退役，禁止执行激活回调。
			this.dependencies.retirePreviewObjects(batchResult.previewObjects);
			return {
				previewTargetEntityIds: [],
				diagnostics: batchResult.diagnostics,
			};
		}

		// 在激活前先登记所有权，保证挂载或标记中途异常时仍能完整退役。
		this.previewEntities = [...batchResult.createdEntities];
		this.previewObjects = [...batchResult.previewObjects];
		try {
			batchResult.activatePreviewEntities?.();
		} catch (error) {
			this.retireTrackedPreviewObjects();
			throw error;
		}

		return {
			previewTargetEntityIds: this.previewEntities.map((entity) => entity.uuid),
			diagnostics: batchResult.diagnostics,
		};
	}

	/**
	 * 清空当前 Preview，但保持命令仍可继续刷新。
	 */
	public clearPreview(): void {
		if (this.disposed) {
			return;
		}

		this.previewRevision += 1;
		this.retireTrackedPreviewObjects();
	}

	/**
	 * 结束当前命令级 Preview 生命周期。
	 *
	 * 该方法允许重复调用；销毁后禁止再次刷新。
	 */
	public dispose(): void {
		if (this.disposed) {
			return;
		}

		this.disposed = true;
		this.previewRevision += 1;
		this.retireTrackedPreviewObjects();
	}

	/**
	 * 读取当前批次拥有的全部 Preview 场景对象快照。
	 *
	 * @returns 根实体、子树对象和外置关联点。
	 */
	public getPreviewEntities(): Object3D[] {
		return [...this.previewObjects];
	}

	/**
	 * 读取当前 Preview 根实体快照。
	 *
	 * @returns 当前 Preview 根实体。
	 */
	public getPreviewEntityRoots(): Object3D[] {
		return [...this.previewEntities];
	}

	/**
	 * 退役当前追踪批次并立即清空本地所有权。
	 */
	private retireTrackedPreviewObjects(): void {
		if (this.previewObjects.length === 0) {
			this.previewEntities = [];
			return;
		}

		const retiringObjects = this.previewObjects;
		this.previewEntities = [];
		this.previewObjects = [];
		this.dependencies.retirePreviewObjects(retiringObjects);
	}

	/**
	 * 阻止已经销毁的命令继续启动异步 Preview。
	 */
	private assertActive(): void {
		if (this.disposed) {
			throw new Error('转换实体引用 PreviewCoordinator 已释放，不能继续刷新');
		}
	}
}
